import { Box, Typography } from '@mui/material';
import { ChevronLeft } from 'lucide-react';
import type { ReactElement } from 'react';

import type { AdminLegalDocumentType } from '@/admin/adminApi';
import { Card, Icon } from '@/ui/kit';

import { EMPTY_DOCUMENT_TEXT, LEGAL_DOCUMENT_TITLES, legalDocumentParagraphs } from './legalDocument';

/**
 * Apercu de l'ecran « CGU et CGV » du mobile (maquette P18).
 *
 * **Fidele au rendu mobile, pas a une idee du rendu** : les paragraphes sont ceux
 * de `legalDocumentParagraphs`, la meme coupe que l'application (lignes blanches,
 * rognage, paragraphes vides ecartes), et le texte est rendu **brut**. Un saut de
 * ligne simple reste un saut de ligne a l'interieur du paragraphe, et les espaces
 * et retraits sont conserves (`pre-wrap`, ce que `react-native-web` pose sur le
 * `Text` ; le natif ne fusionne rien non plus). Aucun Markdown n'est interprete : `**gras**`
 * s'affiche tel quel, comme chez le parent.
 *
 * Un document sans paragraphe affiche la phrase du mobile, « Contenu non
 * disponible pour le moment. ».
 *
 * L'apercu est une illustration : il ne reprend ni la police ni les couleurs
 * exactes de l'application, seulement sa structure.
 */
export const MobilePreview = ({
  type,
  content,
}: {
  readonly type: AdminLegalDocumentType;
  readonly content: string;
}): ReactElement => {
  const paragraphs = legalDocumentParagraphs(content);

  return (
    <Card title="Aperçu" subtitle="Le texte tel que le parent le lira dans l’application.">
      <Box
        role="group"
        aria-label="Aperçu de l’écran CGU et CGV de l’application"
        sx={{
          mx: 'auto',
          width: '100%',
          maxWidth: 280,
          p: 1.75,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          borderRadius: '32px',
          border: 5,
          borderColor: 'tokens.textStrong',
          bgcolor: 'tokens.background',
        }}
      >
        <Box aria-hidden sx={{ alignSelf: 'center', width: 80, height: 6, borderRadius: 3, bgcolor: 'tokens.textStrong' }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'tokens.textStrong' }}>
          <Icon icon={ChevronLeft} size={18} />
          <Typography component="div" sx={{ fontSize: 14, fontWeight: 800 }}>
            CGU et CGV
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            p: 1.5,
            borderRadius: '14px',
            bgcolor: 'tokens.surface',
            maxHeight: 420,
            overflowY: 'auto',
          }}
        >
          <Typography component="div" sx={{ fontSize: 14, fontWeight: 800, color: 'tokens.textStrong' }}>
            {LEGAL_DOCUMENT_TITLES[type]}
          </Typography>
          {paragraphs.length === 0 ? (
            <Typography component="p" sx={{ fontSize: 12, lineHeight: '18px', color: 'tokens.textMuted' }}>
              {EMPTY_DOCUMENT_TEXT}
            </Typography>
          ) : (
            paragraphs.map((paragraph, index) => (
              <Typography
                component="p"
                // Paragraphes recalcules a chaque frappe, jamais reordonnes : l'index suffit.
                key={index}
                data-testid="preview-paragraph"
                sx={{ m: 0, fontSize: 12, lineHeight: '18px', color: 'tokens.textStrong', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
              >
                {paragraph}
              </Typography>
            ))
          )}
        </Box>
      </Box>
    </Card>
  );
};
